import React, { useState } from 'react';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { Formik } from 'formik';
import { useQuery, useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import toast from 'react-hot-toast';
import ToggleForm from '@/components/forms/toggle_form';

import routes from '@/services/routes';
import {
    handleFetchErrors,
    handleValidationErrors,
    onCompletedHandler,
    onErrorHandler,
} from '@/services/apollo';
import Loader from '@/components/commons/loader';
import { SortDirection } from '@/services/sorting';
import OrderableTable from '@/components/commons/orderable-table';
import gridStyles from '@/components/commons/grid/Grid.module.css';
import Button, { ButtonSize, ButtonType } from '@/components/commons/button';
import Pencil from '@/components/commons/icons/Pencil';
import Bars from '@/components/commons/icons/Bars';
import WarningCard from '@/components/commons/warning_card';

import OnboardingTooltip from '@/components/onboarding/OnboardingTooltip';
import {
    FETCH_ALERTS, UPDATE_ALERT, UPDATE_ALERTS_ORDER,
} from './Actions';

export const SORT_COLUMN = 'position';
export const SORT_DIRECTION = SortDirection.ASC;

function AlertsTable() {
    const router = useRouter();

    const [alerts, setAlerts] = useState(null);
    const [isError, setIsError] = useState(false);

    const {
        loading, refetch,
    } = useQuery(FETCH_ALERTS, {
        variables: {
            id: null,
            sortColumn: SORT_COLUMN,
            sortDirection: SORT_DIRECTION,
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
        onError: onErrorHandler(setIsError),
        onCompleted: onCompletedHandler(setIsError, (data) => {
            const alertsResult = data.user.alerts.map((alert) => ({
                id: alert.id,
                name: alert.name,
                pattern: alert.pattern,
                notifications: alert.notifications,
                enabled: alert.enabled,
                position: alert.position,
                editAction: alert.id,
                deleteAction: alert.id,
            }));
            setAlerts(alertsResult);
        }),
    });

    const [formError, setFormError] = useState('');

    const [updateAlert] = useMutation(UPDATE_ALERT);
    const [updateAlertOrder] = useMutation(UPDATE_ALERTS_ORDER);

    const handleUpdateAlert = async (_id, enabled, record) => {
        try {
            const response = await updateAlert({
                variables: {
                    updateAlertInput: {
                        id: record.id,
                        attributes: {
                            enabled,
                        },
                    },
                },
            });
            refetch();

            if (response.data.updateAlert.alert) {
                toast.success('Alert updated successfully.');
                if (formError !== '') setFormError('');
            } else {
                setFormError('Something went wrong.  Please try again.');
            }
        } catch (err) {
            handleFetchErrors(err);
            setFormError('An unexpected error occurred.  Please try again later.');
        }
    };

    const editAlertPositions = async (alertID, newPosition) => {
        try {
            const response = await updateAlertOrder({
                variables: {
                    updateAlertPositionsInput: {
                        attributes: {
                            id: alertID,
                            position: newPosition,
                            sortColumn: SORT_COLUMN,
                            sortDirection: SORT_DIRECTION,
                        },
                    },
                },
            });
            if (response.data.updateAlertPositions.alerts) {
                const updatedAlertsResult = response
                    .data
                    .updateAlertPositions
                    .alerts.map((alert) => ({
                        id: alert.id,
                        name: alert.name,
                        pattern: alert.pattern,
                        notifications: alert.notifications,
                        relativeTo: alert.relativeTo,
                        enabled: alert.enabled,
                        position: alert.position,
                        editAction: alert.id,
                        deleteAction: alert.id,
                    }));
                setAlerts(updatedAlertsResult);
                toast.success('Alert rule updated successfully.');
                if (formError !== '') setFormError('');
                return;
            }
            handleValidationErrors(response.data.updateAlertPositions.errors, () => { }, 'alerts');
        } catch (err) {
            handleFetchErrors(err);
            setFormError('An unexpected error occurred.  Please try again later.');
        }
    };

    const columns = [
        {
            label: '',
            id: 'drag',
            render: (rowId, _record) => (
                <div key={rowId}>
                    <Bars className="h-[20px] w-[20px] text-gray-300" />
                </div>
            ),
        },
        {
            label: 'pattern',
            id: 'pattern',
            render: (rowId, record) => (
                <div key={rowId}>
                    <span className="text-[16px] font-bold text-primary">
                        {' // '}
                        {record.pattern}
                    </span>
                </div>
            ),
        },
        {
            label: 'interval',
            id: 'interval',
            render: (rowId, record) => (
                <div key={rowId}>
                    {record.notifications[0].interval}
                    {' '}
                    {record.notifications[0].period?.replaceAll('_', ' ')}
                    {' '}
                    {record.notifications[0].relativeTo?.replaceAll('_', ' ')}
                </div>
            ),
        },
        {
            label: 'enabled',
            id: 'enabled',
            render: (rowId, record) => {
                const fieldName = `enabled-${record.id}`;
                return (
                    <div key={rowId} className="flex flex-row items-center pb-3 pt-3 text-base font-medium">
                        <Formik
                            onSubmit={() => { }}
                            initialValues={{
                                field: !!record.enabled,
                            }}
                        >
                            <ToggleForm
                                record={record}
                                onChange={handleUpdateAlert}
                                fieldName={fieldName}
                                propertyName="enabled"
                            />
                        </Formik>
                        {record.enabled ? 'Enabled' : 'Disabled'}
                    </div>
                );
            },
        },
        {
            label: '',
            id: 'editAction',
            render: (rowId, record) => (
                <div key={rowId} className="flex items-end justify-end pr-3">
                    <Button
                        externalStyles=""
                        type={ButtonType.secondary}
                        size={ButtonSize.base}
                        text="Edit"
                        onClick={() => router.push({
                            pathname: routes.EDIT_ALERT,
                            query: { id: record.id },
                        })}
                        disabled={false}
                        icon={<Pencil className="h-4 w-4 mr-2 text-cleanSlate" />}
                    />
                </div>
            ),
        },
    ];

    return (
        <Loader isLoading={loading} isError={isError}>
            {alerts && alerts.length
                ? (
                    <>
                        {
                            formError !== '' && (
                                <div className="w-full mb-[20px]">
                                    <WarningCard message={formError} />
                                </div>
                            )
                        }
                        <OrderableTable
                            columns={columns}
                            data={alerts}
                            paginationState={{ windowSize: 3, initialPage: 0 }}
                            editFunc={editAlertPositions}
                            externalStyles="border-separate border-spacing-x-0 border-spacing-y-2"
                        />
                        <Tooltip id="tip" />
                    </>
                ) : (
                    <div className={`border border-dashed flex-col items-center h-[238px] ${gridStyles.emptyAlertAdd}`}>
                        <div className="py-4" />
                        <p className="pb-6 pt-2">You have no alerts.</p>
                    </div>
                )}
            <OnboardingTooltip step={1} placement="bottom" />
        </Loader>
    );
}

export default AlertsTable;
